import axios from 'axios'
import { UserModel } from './_models'

import { API_BASE_URL } from '../../../../config/apiConfig'

const API_URL = process.env.REACT_APP_API_URL
const PA_FASTAPI_BASE = API_BASE_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
/** Admin auth — FastAPI */
export const LOGIN_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/auth/login/`
export const LOGIN_VERIFY_URL = `${API_URL}/auth/confirm-otp`
export const REGISTER_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/auth/signup/`
/** JWT refresh — body: `{ refresh }` only (no access token in body) */
export const TOKEN_REFRESH_URL = `${PA_FASTAPI_BASE}/api/v1/users/auth/token/refresh/`
export const REQUEST_PASSWORD_URL = `${API_URL}/forgot_password`

export const TWO_FACTOR_SETUP_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/auth/2fa/setup/`
export const TWO_FACTOR_VERIFY_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/auth/2fa/verify/`

type AdminLoginTokens = {
  access?: string
  refresh?: string
  token_type?: string
  access_expires_in_seconds?: number
  refresh_expires_in_seconds?: number
}

type AdminLoginPayload = {
  user_id?: number
  username?: string
  email?: string
  user_type?: string
  powers?: string[]
  step_count?: number
  tokens?: AdminLoginTokens
  temp_token?: string
  requires_2fa?: boolean
}

type AdminLoginApiResponse = {
  ok: boolean
  message?: string
  temp_token?: string
  requires_2fa?: boolean
  data?: AdminLoginPayload
}

/** Legacy shape consumed by Login.tsx / saveAuth */
function mapAdminLoginResponse(payload: AdminLoginPayload, rootTempToken?: string) {
  const t = payload.tokens || {}
  const userType = payload.user_type?.toLowerCase() === 'admin' ? 'Admin' : payload.user_type
  const tempToken = payload.temp_token || rootTempToken || ''
  const user = {
    id: payload.user_id,
    user_id: payload.user_id,
    username: payload.username,
    email: payload.email,
    user_type: userType,
    powers: payload.powers,
    step_count: payload.step_count,
  }
  return {
    status: true as const,
    requires_2fa: Boolean(payload.requires_2fa || tempToken),
    temp_token: tempToken,
    data: {
      access_token: t.access || '',
      refresh_token: t.refresh || '',
      token: t.access || '',
      userId: payload.user_id,
      id: payload.user_id,
      username: payload.username,
      email: payload.email,
      user_type: userType,
      role: userType,
      powers: payload.powers,
      step_count: payload.step_count,
      temp_token: tempToken,
      requires_2fa: Boolean(payload.requires_2fa || tempToken),
      user,
    },
  }
}

export async function login(email: string, password: string) {
  try {
    const { data } = await axios.post<any>(LOGIN_URL, {
      email,
      password,
    })

    // If response contains temp_token (2FA required step)
    const tempToken = data?.temp_token || data?.data?.temp_token
    const twoFaStatus = data?.data?.status || data?.status || ''
    const message = data?.message || data?.data?.message || ''

    if (tempToken || twoFaStatus || data?.requires_2fa || data?.data?.requires_2fa) {
      const mappedData = data?.data?.tokens ? mapAdminLoginResponse(data.data, tempToken).data : {}
      return {
        data: {
          ok: data.ok,
          message: message,
          status: twoFaStatus || (tempToken ? 'OTP_REQUIRED' : true),
          two_fa_status: twoFaStatus,
          temp_token: tempToken,
          requires_2fa: true,
          data: {
            ...(data?.data || {}),
            ...mappedData,
            status: twoFaStatus,
            temp_token: tempToken,
            message: message,
          },
        },
      }
    }

    if (!data.ok || !data.data?.tokens?.access) {
      return {
        data: {
          status: false as const,
          message: data.message || 'Login failed',
        },
      }
    }
    return { data: mapAdminLoginResponse(data.data, tempToken) }
  } catch (err: any) {
    const errorMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error?.message ||
      err?.response?.data?.detail ||
      'Invalid email or password. Please try again.'

    return {
      data: {
        status: false as const,
        message: errorMessage,
      },
    }
  }
}

export function loginVerify(peid: string, otp: string) {
  return axios.post(LOGIN_VERIFY_URL, { pe_id: peid, otp_code: otp })
}

export function register(email: string, firstname: string, lastname: string, password: string) {
  return axios.post(REGISTER_URL, {
    email,
    password,
    first_name: firstname,
    last_name: lastname,
  })
}

export function requestPassword(email: string) {
  return axios.post<{ result: boolean }>(REQUEST_PASSWORD_URL, {
    email,
  })
}

export function getUserByToken(token: string) {
  return axios.post<UserModel>(GET_USER_BY_ACCESSTOKEN_URL, {
    access_token: token,
  })
}

export const ADMIN_DASHBOARD_STATS_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/stats/`

export type AdminDashboardStatsData = {
  total_users: number
  user_profiles_customers: number
  user_profiles_panel_admins: number
  panel_super_admins: number
  panel_delegated_staff: number
  blocked_users: number
  roles_defined: number
}

type AdminDashboardStatsApiResponse = {
  ok: boolean
  message?: string
  data?: AdminDashboardStatsData
}

export async function getAdminDashboardStats(): Promise<AdminDashboardStatsData> {
  const { data } = await axios.get<AdminDashboardStatsApiResponse>(ADMIN_DASHBOARD_STATS_URL)
  if (!data.ok || !data.data) {
    throw new Error(data.message || 'Failed to load dashboard stats')
  }
  return data.data
}

export const ADMIN_ME_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/me/`

export const ADMIN_STAFF_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/staff/`

export type CreateAdminStaffBody = {
  email: string
  password: string
  first_name: string
  last_name: string
  role_id: number
}

export type AdminStaffCreated = {
  user_id: number
  username: string
  email: string
  role: AdminRole
}

type AdminStaffCreateApiResponse = {
  ok: boolean
  message?: string
  data?: AdminStaffCreated
}

export async function createAdminStaff(body: CreateAdminStaffBody): Promise<AdminStaffCreated> {
  const { data } = await axios.post<AdminStaffCreateApiResponse>(ADMIN_STAFF_URL, body)
  if (!data.ok || !data.data) {
    throw new Error(
      typeof data.message === 'string' && data.message.trim()
        ? data.message
        : 'Failed to create staff user'
    )
  }
  return data.data
}

export type AdminMeUser = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_blocked: boolean
}

export type AdminMePayload = {
  user: AdminMeUser
  is_super_admin: boolean
  role: string | null
  effective_powers: string[]
}

type AdminMeApiResponse = {
  ok: boolean
  message?: string
  data?: AdminMePayload
}

export async function getAdminMe(token?: string): Promise<AdminMePayload> {
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
  const { data } = await axios.get<AdminMeApiResponse>(ADMIN_ME_URL, config)
  if (!data.ok || !data.data) {
    throw new Error(data.message || 'Failed to load admin profile')
  }
  return data.data
}

export function mapAdminMeToProfileData(payload: AdminMePayload) {
  const u = payload.user
  const roleKey = payload.role || (payload.is_super_admin ? 'SUPER_ADMIN' : 'Admin')
  const displayRole = payload.role || (payload.is_super_admin ? 'Super Admin' : 'Admin')
  return {
    id: u.id,
    uuid: u.id,
    username: u.username,
    email: u.email,
    name: u.first_name || u.username || '',
    lastname: u.last_name || '',
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    user_type: displayRole,
    is_active: !u.is_blocked,
    is_super_admin: payload.is_super_admin,
    role: roleKey,
    effective_powers: payload.effective_powers,
    mobile_number: '',
  }
}

export type AdminMeUpdateBody = {
  first_name?: string
  last_name?: string
  email?: string
}

export async function updateAdminMe(
  body: AdminMeUpdateBody
): Promise<{ profile: AdminMePayload; message?: string }> {
  const { data } = await axios.patch<AdminMeApiResponse>(ADMIN_ME_URL, body)
  if (!data.ok || !data.data) {
    throw new Error(data.message || 'Failed to update profile')
  }
  return { profile: data.data, message: data.message }
}

export async function changeAdminPassword(
  current: string,
  newPass: string
): Promise<{ message: string }> {
  const { data } = await axios.patch<AdminMeApiResponse>(ADMIN_ME_URL, {
    current_password: current,
    password: newPass,
  })
  if (!data.ok) {
    throw new Error(data.message || 'Failed to change password')
  }
  return { message: data.message || 'Password changed successfully' }
}

export const ADMIN_PERMISSIONS_CATALOG_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/permissions/`

export const ADMIN_ROLES_URL = `${PA_FASTAPI_BASE}/api/v1/users/admin/roles/`

/** Role row returned by FastAPI admin roles API */
export type AdminRole = {
  id: number
  slug: string
  display_name: string
  description: string | null
  permission_codes: string[]
  created_at: string
  updated_at: string
}

export type CreateAdminRoleBody = {
  slug: string
  display_name: string
  description?: string
  permission_codes: string[]
}

/** URL-safe slug from display name (e.g. "Support Team" → "support-team"). */
export function slugifyRoleSlug(name: string): string {
  const s = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return s || 'role'
}

export type AdminRolesListApiResponse = {
  ok: boolean
  message?: string
  data?: { roles: AdminRole[] }
}

export async function getAdminRoles(): Promise<AdminRole[]> {
  const { data } = await axios.get<AdminRolesListApiResponse>(ADMIN_ROLES_URL)
  if (!data.ok || !data.data?.roles) {
    throw new Error(data.message || 'Failed to load roles')
  }
  return data.data.roles
}

export type AdminRoleDetailApiResponse = {
  ok: boolean
  message?: string
  data?: { role: AdminRole }
}

export function getAdminRoleDetailUrl(adminRoleId: string | number) {
  const id = encodeURIComponent(String(adminRoleId))
  return `${ADMIN_ROLES_URL}${id}/`
}

export async function getAdminRoleById(adminRoleId: string | number): Promise<AdminRole> {
  const { data } = await axios.get<AdminRoleDetailApiResponse>(getAdminRoleDetailUrl(adminRoleId))
  if (!data.ok || !data.data?.role) {
    throw new Error(data.message || 'Failed to load role')
  }
  return data.data.role
}

export async function createAdminRole(body: CreateAdminRoleBody): Promise<AdminRole> {
  const { data } = await axios.post<{
    ok: boolean
    message?: string
    data?: { role: AdminRole }
  }>(ADMIN_ROLES_URL, body)
  if (!data.ok || !data.data?.role) {
    throw new Error(data.message || 'Failed to create role')
  }
  return data.data.role
}

export type UpdateAdminRoleBody = {
  slug?: string
  display_name?: string
  description?: string
  permission_codes?: string[]
}

export async function updateAdminRole(
  adminRoleId: string | number,
  body: UpdateAdminRoleBody
): Promise<AdminRole> {
  const { data } = await axios.patch<AdminRoleDetailApiResponse>(
    getAdminRoleDetailUrl(adminRoleId),
    body
  )
  if (!data.ok || !data.data?.role) {
    throw new Error(data.message || 'Failed to update role')
  }
  return data.data.role
}

/** FastAPI error bodies: `{ ok: false, message, error: { code, message } }` */
export function parseAdminApiErrorMessage(data: unknown): string {
  if (data == null || typeof data !== 'object') return 'Request failed'
  const d = data as {
    message?: string
    error?: { message?: string; code?: string }
  }
  return (
    (typeof d.message === 'string' && d.message) ||
    (typeof d.error?.message === 'string' && d.error.message) ||
    'Request failed'
  )
}

export async function deleteAdminRole(adminRoleId: string | number): Promise<void> {
  const url = getAdminRoleDetailUrl(adminRoleId)
  try {
    const { data, status } = await axios.delete<{
      ok: boolean
      message?: string
      error?: { code?: string; message?: string }
    }>(url)
    if (status === 204 || data == null) return
    if (typeof data === 'object' && data.ok === false) {
      throw new Error(parseAdminApiErrorMessage(data))
    }
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data) {
      throw new Error(parseAdminApiErrorMessage(e.response.data))
    }
    throw e
  }
}

export function mapAdminRoleToListRow(r: AdminRole) {
  const perms = r.permission_codes.map((code) => ({
    permission: { name: code, uuid: code },
    can_read: true,
    can_write: true,
    can_edit: true,
  }))
  return {
    uuid: String(r.id),
    name: r.display_name,
    description: r.description ?? undefined,
    created_at: r.created_at,
    is_active: true,
    available_permissions: perms,
    group_permissions: perms,
    permission_codes: r.permission_codes,
    slug: r.slug,
  }
}

export type AdminPowerCatalogItem = {
  code: string
  label: string
}

export async function getAdminPermissionsCatalog(): Promise<AdminPowerCatalogItem[]> {
  const { data } = await axios.get<{
    ok: boolean
    message?: string
    data?: { powers: AdminPowerCatalogItem[] }
  }>(ADMIN_PERMISSIONS_CATALOG_URL)
  if (!data.ok || !data.data?.powers) {
    throw new Error(data.message || 'Failed to load permissions catalog')
  }
  return data.data.powers
}

export type PermissionRowFromCatalog = {
  uuid: string
  name: string
  label: string
  can_read: boolean
  can_write: boolean
  can_edit: boolean
}

export function buildPermissionRowsFromCatalog(
  powers: AdminPowerCatalogItem[],
  getExisting?: (
    code: string
  ) => { can_read?: boolean; can_write?: boolean; can_edit?: boolean } | undefined
): PermissionRowFromCatalog[] {
  return powers.map((p) => {
    const ex = getExisting?.(p.code)
    return {
      uuid: p.code,
      name: p.code,
      label: p.label,
      can_read: ex?.can_read ?? false,
      can_write: ex?.can_write ?? false,
      can_edit: ex?.can_edit ?? false,
    }
  })
}

export async function exampleProtectedFastApiGet(path: string) {
  const url = path.startsWith('http')
    ? path
    : `${PA_FASTAPI_BASE}${path.startsWith('/') ? '' : '/'}${path}`
  return axios.get(url)
}
